/**
 * InventoryDisclosure — the §6.8 coverage inventory, read and not-read
 * (contracts-plan.md §13.8 WP-74, G9: "nothing unread is silent").
 *
 * Reads `parseInventory.inventory` DEFENSIVELY: the shape differs by parser
 * (xlsx: {reader, interpret}; docx: {reader, interpret: {clauses}}; pdf:
 * {reader, execution, interpret?}) and every access below uses optional
 * chaining so an unfamiliar shape renders its own keys as plain key/value
 * lines rather than throwing or being dropped (this WP's brief item 7).
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import type { ParseInventory } from '../../api/versions';

export interface InventoryDisclosureProps {
  versionId: string;
  parseInventory: ParseInventory | null;
  parseStatus: string;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function labelOf(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `${value.length} item(s)`;
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.length === 0) return '{}';
  // A flat object of scalars (a count map, a skipped part) shows its values, not only its keys.
  const flat = keys.every((k) => ['string', 'number', 'boolean'].includes(typeof record[k]));
  return flat ? keys.map((k) => `${k}: ${String(record[k])}`).join(', ') : `{ ${keys.join(', ')} }`;
}

/** Every reader/interpret count key rendered generically — unrecognised keys still show, per G9. */
function CountsBlock({ counts }: { counts: Record<string, unknown> | null }) {
  if (!counts) return null;
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1 sm:grid-cols-3">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-2 py-1 text-xs dark:bg-gray-900/40">
          <dt className="text-gray-500 dark:text-gray-400">{key}</dt>
          <dd className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{labelOf(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

const TOGGLE_CLS =
  'text-xs font-semibold text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200';

/**
 * Skipped rows grouped by the parser's own `reason` wording (backend
 * interpret/grid.js), in the order a reader should look at them: the
 * unlabelled group first, because it is the only one that could hide a priced
 * line. A group with no rows is never rendered.
 */
type RowGroupKey = 'unlabelled' | 'totals' | 'headings' | 'notes' | 'other';
const ROW_GROUP_ORDER: RowGroupKey[] = ['unlabelled', 'totals', 'headings', 'notes', 'other'];

function rowGroupOf(row: unknown): RowGroupKey {
  const reason = asRecord(row)?.reason;
  const text = typeof reason === 'string' ? reason.trim().toLowerCase() : '';
  if (text.startsWith('a section heading')) return 'headings';
  if (text.startsWith('a heading or a note')) return 'notes';
  if (text.startsWith('a total row')) return 'totals';
  if (text.startsWith('numbers with no label')) return 'unlabelled';
  return 'other';
}

function rowGroupLine(key: RowGroupKey, n: number): string {
  const one = n === 1;
  switch (key) {
    case 'unlabelled':
      return `${n} ${one ? 'row' : 'rows'} with amounts but no label - check these`;
    case 'totals':
      return `${n} total ${one ? 'row' : 'rows'} (they add up the lines above)`;
    case 'headings':
      return `${n} section heading ${one ? 'row' : 'rows'}`;
    case 'notes':
      return `${n} heading or note ${one ? 'row' : 'rows'} with no amount`;
    default:
      return `${n} other skipped ${one ? 'row' : 'rows'}`;
  }
}

function countOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function InventoryDisclosure({ versionId, parseInventory, parseStatus }: InventoryDisclosureProps) {
  const [expanded, setExpanded] = useState(false);
  const [partsOpen, setPartsOpen] = useState(false);
  const [techOpen, setTechOpen] = useState(false);
  const [openRowGroups, setOpenRowGroups] = useState<Record<string, boolean>>({});

  if (!parseInventory) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400" data-testid={`ver-inventory-${versionId}`}>
        Not parsed yet (parse status: {parseStatus}).
      </p>
    );
  }

  const inventory = parseInventory.inventory;
  const reader = asRecord(asRecord(inventory)?.reader);
  const interpret = asRecord(inventory)?.interpret;
  const interpretRecord = asRecord(interpret);
  const readerCounts = asRecord(reader?.counts);
  const interpretCounts = interpretRecord ? asRecord(interpretRecord.counts) : null;

  const unread = asArray(reader?.unread);
  const partsSkipped = asArray(asRecord(reader?.package)?.parts_skipped);
  const sheetsNotRead = asArray(interpretRecord?.sheets).filter((s) => asRecord(s)?.read === false);
  const skippedRows = asArray(interpretRecord?.sheets).flatMap((s) => asArray(asRecord(s)?.skipped));
  const rowGroups = ROW_GROUP_ORDER.map((key) => ({ key, rows: skippedRows.filter((r) => rowGroupOf(r) === key) })).filter(
    (g) => g.rows.length > 0,
  );
  const unattachedFlags = asArray(interpretRecord?.unattached_flags);
  const errors = asArray(parseInventory.errors);

  const notSegmented = typeof interpret === 'string' ? interpret : (interpretRecord?.clauses === 'not_segmented');
  const unreadTotal =
    unread.length + partsSkipped.length + sheetsNotRead.length + skippedRows.length + unattachedFlags.length + errors.length;

  // Plain summary: built ONLY from values the inventory already carries; a
  // sentence whose value is absent is left out, never filled in.
  const linesRead = countOf(interpretCounts?.lines);
  const sheetsRead = countOf(interpretCounts?.sheets_read);
  const unmatched = countOf(asRecord(interpretCounts?.match_status)?.unmatched) ?? countOf(interpretCounts?.unmatched);
  const notesSaved = countOf(interpretCounts?.notes);
  const summaryLines: string[] = [];
  if (linesRead !== null) {
    summaryLines.push(
      `Read ${linesRead} budget ${linesRead === 1 ? 'line' : 'lines'}${
        sheetsRead !== null ? ` from ${sheetsRead} ${sheetsRead === 1 ? 'sheet' : 'sheets'}` : ''
      }.`,
    );
  }
  if (unmatched !== null) {
    summaryLines.push(`${unmatched} ${unmatched === 1 ? 'line' : 'lines'} not yet linked to your master list.`);
  }
  if (notesSaved !== null) {
    summaryLines.push(`${notesSaved} ${notesSaved === 1 ? 'comment' : 'comments'} saved as notes.`);
  }
  const hasCounts = (readerCounts ? Object.keys(readerCounts).length : 0) + (interpretCounts ? Object.keys(interpretCounts).length : 0) > 0;
  const showSummary = summaryLines.length > 0 || Boolean(parseInventory.templateFamily);

  // Any top-level inventory key this component does not otherwise render —
  // shown as a plain key/value line rather than dropped (G9).
  const knownKeys = new Set(['reader', 'interpret', 'execution']);
  const otherKeys = Object.entries(asRecord(inventory) ?? {}).filter(([key]) => !knownKeys.has(key));

  return (
    <div data-testid={`ver-inventory-${versionId}`}>
      <button
        type="button"
        data-testid={`ver-inventory-toggle-${versionId}`}
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[44px] items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200 sm:min-h-0"
        title={`Coverage inventory — §6.8, G9: nothing unread is silent (family: ${parseInventory.templateFamily ?? 'unknown'}${
          parseInventory.familyLabel ? `, ${parseInventory.familyLabel}` : ''
        })`}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        Coverage inventory {unreadTotal > 0 && <span className="text-amber-600 dark:text-amber-400">({unreadTotal} unread)</span>}
      </button>

      {expanded && (
        <div className="mt-2 grid grid-cols-1 gap-3 rounded-lg border border-gray-200 p-3 text-xs dark:border-gray-700 sm:grid-cols-2">
          {showSummary && (
            <section className="space-y-0.5 text-sm text-gray-700 dark:text-gray-200 sm:col-span-2" data-testid={`ver-inventory-summary-${versionId}`}>
              {summaryLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {parseInventory.templateFamily && (
                <p>
                  Recognised as:{' '}
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
                    title={`family — ${parseInventory.familyLabel ?? 'extracted'} (G8)`}
                  >
                    {parseInventory.templateFamily}
                  </span>
                </p>
              )}
            </section>
          )}

          <section>
            <h5 className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-green-700 dark:text-green-400">
              <CheckCircle2 size={12} /> Read
            </h5>
            {hasCounts && (
              <div>
                <button
                  type="button"
                  data-testid={`ver-inventory-tech-toggle-${versionId}`}
                  aria-expanded={techOpen}
                  onClick={() => setTechOpen((v) => !v)}
                  className={`flex min-h-[44px] items-center gap-1 sm:min-h-0 ${TOGGLE_CLS}`}
                >
                  {techOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  Technical details
                </button>
                {techOpen && (
                  <div className="mt-1 space-y-1.5">
                    <CountsBlock counts={readerCounts} />
                    <CountsBlock counts={interpretCounts} />
                  </div>
                )}
              </div>
            )}
            {notSegmented && (
              <p className="mt-1.5 text-gray-600 dark:text-gray-300">
                Clauses: not segmented ({interpretRecord?.reason ? String(interpretRecord.reason) : 'clause segmenter not wired'}).
              </p>
            )}
            {typeof interpretRecord?.clauses === 'number' && (
              <p className="mt-1.5 text-gray-600 dark:text-gray-300">Clauses read: {String(interpretRecord.clauses)}</p>
            )}
          </section>

          <section>
            <h5 className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-700 dark:text-amber-400">
              <AlertTriangle size={12} /> Not read
            </h5>
            {unreadTotal === 0 ? (
              <p className="text-gray-500 dark:text-gray-400">Nothing unread was reported.</p>
            ) : (
              <ul className="space-y-1">
                {unread.map((item, i) => (
                  <li key={`unread-${i}`} data-testid={`ver-inventory-unread-${versionId}-${i}`}>
                    {labelOf(item)}
                  </li>
                ))}
                {partsSkipped.length > 0 && (
                  <li key="parts-folded">
                    {partsSkipped.length} technical file {partsSkipped.length === 1 ? 'part' : 'parts'} skipped, no budget content{' '}
                    <button
                      type="button"
                      data-testid={`ver-inventory-parts-toggle-${versionId}`}
                      aria-expanded={partsOpen}
                      onClick={() => setPartsOpen((v) => !v)}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {partsOpen ? 'Hide details' : 'Show details'}
                    </button>
                    {partsOpen && (
                      <ul className="mt-1 space-y-1 pl-3">
                        {partsSkipped.map((item, i) => (
                          <li key={`part-${i}`}>
                            part skipped:{' '}
                            {typeof item === 'string' ? item : `${String(asRecord(item)?.part ?? '?')}: ${String(asRecord(item)?.reason ?? 'skipped')}`}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
                {sheetsNotRead.map((sheet, i) => (
                  <li key={`sheet-${i}`}>
                    sheet {String(asRecord(sheet)?.name ?? '?')}: {String(asRecord(sheet)?.reason ?? 'not read')}
                  </li>
                ))}
                {rowGroups.map(({ key, rows }) => {
                  const open = openRowGroups[key] === true;
                  return (
                    <li key={`rows-${key}`} className={key === 'unlabelled' ? 'text-amber-700 dark:text-amber-400' : undefined}>
                      {rowGroupLine(key, rows.length)}{' '}
                      <button
                        type="button"
                        data-testid={`ver-inventory-rows-toggle-${versionId}-${key}`}
                        aria-expanded={open}
                        onClick={() => setOpenRowGroups((prev) => ({ ...prev, [key]: !prev[key] }))}
                        className={TOGGLE_CLS}
                      >
                        {open ? 'Hide details' : 'Show details'}
                      </button>
                      {open && (
                        <ul className="mt-1 space-y-1 pl-3 text-gray-700 dark:text-gray-200">
                          {rows.map((row, i) => {
                            const label = asRecord(row)?.label;
                            const name = typeof label === 'string' ? label.trim() : '';
                            return (
                              <li key={`row-${key}-${i}`}>
                                Row {String(asRecord(row)?.row ?? '?')}
                                {name.length > 0 ? `: ${name}` : ''}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
                {unattachedFlags.map((flag, i) => (
                  <li key={`flag-${i}`}>unattached flag: {labelOf(flag)}</li>
                ))}
                {errors.map((err, i) => (
                  <li key={`err-${i}`} className="text-red-600 dark:text-red-400">
                    error: {labelOf(err)}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {otherKeys.length > 0 && (
            <section className="sm:col-span-2">
              <h5 className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Other</h5>
              <ul className="space-y-0.5">
                {otherKeys.map(([key, value]) => (
                  <li key={key}>
                    {key}: {labelOf(value)}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

export default InventoryDisclosure;
